import { type ForwardRefRenderFunction, type HTMLAttributes, forwardRef, useRef } from "react";
import {
	type AriaCheckboxProps,
	VisuallyHidden,
	mergeProps,
	useCheckbox,
	useFocusRing,
} from "react-aria";
import { useToggleState } from "react-stately";

import { CheckmarkIcon, SubtractIcon } from "@moment/icons";

import { cn } from "../utils";

export type InputCheckboxProps = AriaCheckboxProps & {
	className?: string;
	size?: "sm" | "md" | "lg";
};

const BaseInputCheckbox: ForwardRefRenderFunction<
	HTMLInputElement,
	InputCheckboxProps & Omit<HTMLAttributes<HTMLButtonElement>, "onChange">
> = ({ className, size = "md", ...props }, ref) => {
	// Hooks
	const state = useToggleState(props);
	const internalRef = useRef<HTMLInputElement | null>(null);
	const { inputProps } = useCheckbox(props, state, internalRef);
	const { focusProps } = useFocusRing();

	// Variables
	const isSelected = state.isSelected || props.isIndeterminate;
	const checkColor = isSelected ? "text-inverse-primary" : "opacity-0";
	const focusPrimaryClassList =
		"ring-default focus:outline-none focus:border-brand focus:ring-4 focus:shadow-none";

	return (
		<label
			className={cn(
				"flex items-center gap-2 cursor-pointer",
				"text-primary",
				size === "sm" && "text-sm",
				size === "md" && "text-md",
				size === "lg" && "text-lg"
			)}
		>
			<VisuallyHidden>
				<input
					{...mergeProps(inputProps, focusProps)}
					ref={(el) => {
						internalRef.current = ref !== null && "current" in ref ? ref.current : el;
					}}
				/>
			</VisuallyHidden>
			<button
				type="button"
				aria-label={props["aria-label"] ?? "Select option"}
				className={cn(
					"rounded",
					focusPrimaryClassList,
					className,
					"inline-block",
					"relative",
					"flex-[0_0_auto]",
					"flex-none",
					"bg-primary",
					"shadow-border-dropshadow-default",
					"hover:shadow-border-dropshadow-hover",
					"focus:shadow-border-dropshadow-focus",
					"active:shadow-border-dropshadow-active",
					"disabled:shadow-border-dropshadow-disabled",
					"disabled:pointer-events-none",
					"disabled:bg-button-normal-secondary-disabled",
					isSelected && "border-brand bg-brand hover:bg-button-normal-primary-hover",
					isSelected &&
						"disabled:pointer-events-none disabled:bg-button-normal-primary-disabled disabled:text-button-normal-primary-disabled",
					size === "sm" && "h-4 w-4",
					size === "md" && "h-6 w-6",
					size === "lg" && "h-8 w-8"
				)}
				onClick={() => props.onChange?.(!isSelected)}
				disabled={props.isDisabled}
			>
				{props.isIndeterminate ? (
					// @ts-ignore: Carbon icon type compatibility issue
					<SubtractIcon
						className={cn(checkColor, "absolute left-0 top-0 h-full w-full")}
						size={16}
					/>
				) : (
					// @ts-ignore: Carbon icon type compatibility issue
					<CheckmarkIcon
						className={cn(checkColor, "absolute left-0 top-0 h-full w-full")}
						size={16}
					/>
				)}
			</button>
			{props.children}
		</label>
	);
};

export const InputCheckbox = forwardRef(BaseInputCheckbox);
